<template>
  <DefaultPanel
    v-if="isReady"
    :empty="ordersCount === 0"
    :loading="isLoadingOrders"
  >
    <template #header>
      <span class="icon">
        <i class="fa-solid fa-shopping-cart" />
      </span>
      <span>Orders</span>
      <span class="tag tag-count is-rounded is-primary">
        {{ ordersCount }}
      </span>
      <span v-if="ordersCount > 0 && pendingCount > 0">
        ({{ pendingCount }} pending)
      </span>
    </template>
    <div v-if="hasError" class="order-error">
      <span class="icon has-text-danger">
        <i class="fa-solid fa-circle-exclamation" />
      </span>
      <span>Could not load orders.</span>
    </div>
    <div v-else-if="ordersCount > 0" class="order-list">
      <div v-for="order in orders" :key="order.id.value" class="order-entry">
        <div class="order-header">
          <router-link
            :to="{
              name: 'orderDetail',
              params: { id: order.order.value.id },
            }"
            class="order-name"
          >
            {{ order.order.value.name }}
          </router-link>
        </div>
        <div class="order-details">
          <span
            v-if="order.catalogue_number?.value || order.batch_number?.value"
          >
            <b v-tooltip="fieldMeta.catalogue_number?.label">Cat#:</b>
            <FieldComponent
              v-if="order.catalogue_number?.value"
              class="is-inline-block"
              :field-meta="fieldMeta.catalogue_number"
              :data="order.catalogue_number"
            />
            <template v-if="order.batch_number?.value">
              <span class="sep">/</span>
              <b v-tooltip="fieldMeta.batch_number?.label">Batch#:</b>
              <FieldComponent
                class="is-inline-block"
                :field-meta="fieldMeta.batch_number"
                :data="order.batch_number"
              />
            </template>
            <template v-if="order.external_id?.value">
              <span class="sep">/</span>
              <b v-tooltip="fieldMeta.external_id?.label">Ext#:</b>
              <FieldComponent
                class="is-inline-block"
                :field-meta="fieldMeta.external_id"
                :data="order.external_id"
              />
            </template>
          </span>
          <span v-if="order.vendor?.value">
            <b>Vendor:</b>
            <FieldComponent
              class="is-inline-block"
              :field-meta="fieldMeta.vendor"
              :data="order.vendor"
            />
          </span>
          <span v-if="order.price?.value > 0">
            <b>Price:</b>
            <FieldComponent
              class="is-inline-block"
              :field-meta="fieldMeta.price"
              :data="order.price"
            />
            <span v-if="order.currency?.value" class="order-currency">
              {{ order.currency.value?.name ?? order.currency.value }}
            </span>
          </span>
          <span v-if="order.quantity?.value">
            <b>Qty:</b>
            <FieldComponent
              class="is-inline-block"
              :field-meta="fieldMeta.quantity"
              :data="order.quantity"
            />
          </span>
          <span v-if="order.total_price?.value > 0">
            <b>Total:</b>
            <FieldComponent
              class="is-inline-block"
              :field-meta="fieldMeta.total_price"
              :data="order.total_price"
            />
            <span v-if="order.currency?.value" class="order-currency">
              {{ order.currency.value?.name ?? order.currency.value }}
            </span>
          </span>
        </div>
        <div
          v-if="order.ordered_for?.value || order.created?.value"
          class="order-meta"
        >
          <span>Ordered</span>
          <span
            v-if="order.created?.value"
            v-tooltip="dateFormatted(order.created.value)"
          >
            {{ dateDisplay(order.created.value) }}
          </span>
          <template v-if="order.ordered_for?.value">
            <span>for</span>
            <FieldComponent
              class="is-inline-block"
              :field-meta="fieldMeta.ordered_for"
              :data="order.ordered_for"
            />
          </template>
          <template
            v-if="
              order.modified?.value &&
              order.modified.value !== order.created?.value
            "
          >
            <span class="sep">·</span>
            <span v-tooltip="dateFormatted(order.modified.value)">
              updated {{ dateDisplay(order.modified.value) }}
            </span>
          </template>
        </div>
      </div>
    </div>
  </DefaultPanel>
</template>

<script>
import { mapGetters } from 'vuex'

import { get } from '@/api'
import { momentDate } from '@/utils'

import DefaultPanel from '@/components/DefaultPanel.vue'

const ORDERED_ITEM_FIELDS = [
  'catalogue_number',
  'batch_number',
  'external_id',
  'vendor',
  'price',
  'quantity',
  'total_price',
  'ordered_for',
  'created',
  'modified',
]

export default {
  components: {
    DefaultPanel,
  },
  props: {
    appName: {
      type: String,
      default: 'stocks',
    },
    stockId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      orders: null,
      isLoadingOrders: false,
      hasError: false,
    }
  },
  async mounted() {
    this.orders = await this.loadOrders()
  },
  computed: {
    ...mapGetters('meta', ['getMeta']),
    itemMeta() {
      return this.getMeta('stocks', 'ordereditem', 'DEFAULT')
    },
    isReady() {
      return !!this.itemMeta.model?.attributes?.length
    },
    fetchOrderUrl() {
      return (
        this.stockId &&
        `${this.appName}/ordereditems/?stock=${this.stockId}&page_size=max`
      )
    },
    fieldMeta() {
      const result = {}
      for (const name of ORDERED_ITEM_FIELDS) {
        const model = this.itemMeta.model?.attributes?.find(
          (x) => x.name === name,
        )
        const view = this.itemMeta.view?.detail?.attributes?.find(
          (x) => x.name === name,
        )
        result[name] = { ...model, ...view }
      }
      return result
    },
    ordersCount() {
      return this.orders?.length || 0
    },
    pendingCount() {
      // 'ordered' = placed but not yet received
      return (
        this.orders?.filter(
          (item) => item.status.value?.value?.toLowerCase() === 'ordered',
        )?.length || 0
      )
    },
  },
  methods: {
    dateDisplay(val) {
      const m = momentDate(val)
      if (!m) return val
      return m.diff(new Date(), 'days') >= -7
        ? m.fromNow()
        : `on ${m.format('MMM D, YYYY')}`
    },
    dateFormatted(val) {
      return momentDate(val)?.format('MMMM Do YYYY, HH:mm') ?? val
    },
    async loadOrders() {
      if (!this.fetchOrderUrl) return null
      let orders = null
      try {
        this.isLoadingOrders = true
        const response = await get(this.fetchOrderUrl)
        if (response.status === 200) orders = response.data.results
      } catch {
        this.hasError = true
      } finally {
        this.isLoadingOrders = false
      }
      return orders
    },
  },
}
</script>

<style scoped>
.order-error {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.order-list {
  width: 100%;
}

.order-entry {
  padding: var(--space-3) var(--space-3);
}

.order-entry:not(:last-child) {
  border-bottom: 1px solid var(--border-default);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-1);
}

.order-name {
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.order-details {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-4);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.order-details b {
  margin-right: 0.2em;
  color: var(--text-default);
}

.order-details :deep(.field-resolver) {
  display: inline;
}

.order-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 0.3em;
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: var(--space-1);
  line-height: var(--leading-normal);
}

.order-meta :deep(.field-resolver),
.order-meta :deep(.field-widget),
.order-meta :deep(.field-widget-appear) {
  display: inline;
}

.sep {
  margin: 0 0.2em;
  color: var(--text-subtle);
}

.order-currency {
  margin-left: 0.2em;
  color: var(--text-subtle);
}
</style>
